import { Component, Prop } from 'vue-property-decorator'
import Vue, { CreateElement } from '@src/vue'
import QrcodeVue from 'qrcode.vue'

import { Body1, H6, Overline, Subtitle1 } from '@src/elements/typography/typography'
import { Card } from '@src/components/card/card'
import { Icon } from '@src/elements/icon/icon'
import AssignToMember from '@src/pages/assign-to-member/assign-to-member'
import { DefaultPreviewImageDTO, PreviewImage, PreviewImageDTO } from '@src/routes/content/components/workouts/preview-image'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'
import { Popover } from '@src/components/popover/popover'
import { OptionGroup } from '@src/elements/select/option-group'
import { DropdownOption } from '@src/elements/select/option'
import { MenuButton } from '@src/elements/button/button'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { Modal } from '@src/elements/modal/modal'
import { ExerciseResponse } from '@src/data/exercise-response'
import WorkoutData, { HolderType, WorkoutType } from '@src/data/workout-data'
import { WorkoutExercise } from '@src/data/workout-exercise'
import Member from '@src/data/member'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { WorkoutService } from '@src/service/workout'
import EventBus from '@src/lib/event_bus'
import { snakecaseKeys } from '@src/util/functions'
import { NotificationTypes } from '@src/util/constants'
import camelcaseKeys from 'camelcase-keys'
import SessionManager from '@src/lib/session_manager'
import { convertDateTo } from '@src/util/date-format'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { LoadingScreen } from '@src/components/loading-screen/loading-screen/loading-screen'
import SelectCategories from '@src/routes/content/components/categories/select-categories'
import { PrintExercises } from '@src/components/print/print-exercises'

import '../workout-card.sass'

import { ExerciseCardInfo } from '@src/components/card/exercise-card/exercise-card-info/exercise-card-info'
import ClickOutside from 'vue-click-outside'
import { MobileTooltip } from '@src/elements/tooltip/mobile-tooptip'
import { PositionChanger } from '@src/elements/position-changer/position-changer'
import { WorkoutRuns } from '@src/components/card/workout-card/workout-runs/workout-runs'

@Component(
  {
    directives: {
      ClickOutside
    }
  }
)
export class WorkoutCardInfo extends Vue {

  @Prop({ required: false, default: 0 })
  index: number

  @Prop({ required: false, type: Function })
  onPositionChange?: (data: { oldIndex: number, newPosition: number }) => void

  @Prop({ required: true })
  workout: WorkoutData

  @Prop({ required: false })
  workoutCount: number

  @Prop({ required: true })
  type: 'view' | 'active' | 'select' | 'stats'

  @Prop({ required: false, default: false })
  isSelected: boolean

  @Prop({ required: false })
  updateCallback: (id: number | null, memberId: number | null) => void

  @Prop({ required: false, default: false })
  readonly: boolean

  @Prop({ required: false })
  member: Member

  @Prop({ required: false })
  runs?: number

  apiClient: ApiClient = new ApiClient()

  protected confirmMessage: string = ''
  protected notificationType: string = ''

  protected showConfirmPopup: boolean = false
  protected showPrintExercises: boolean = false
  protected showEquipmentPopup: boolean = false
  private showPreviewImage: boolean = false
  private previewImageProcessing: boolean = false

  private service: WorkoutService = new WorkoutService()
  private showCategories: boolean = false
  private workoutExercises: WorkoutExercise[] = []
  private exBreakPoints: boolean[] = []

  private exercises: ExerciseResponse[] = []

  private showLoading: boolean = false

  private showExercisesPopup: boolean = false
  private showWorkoutCardDropdownOptions: boolean = false

  private printMediaQueryList: MediaQueryList | null = null
  private afterPrintListener: (() => void) | null = null
  private printMediaListener: ((mql: { matches: boolean }) => void) | null = null

  constructor () {
    super()
  }

  created () {
    // after print, reset workout to be printed
    this.afterPrintListener = () => this.resetPrint()
    window.addEventListener('afterprint', this.afterPrintListener)
    this.printMediaListener = (mql) => {
      if (!mql.matches) {
        this.resetPrint()
      }
    }
    this.printMediaQueryList = window.matchMedia('print')
    this.printMediaQueryList.addListener(this.printMediaListener)
  }

  beforeDestroy () {
    if (this.afterPrintListener) window.removeEventListener('afterprint', this.afterPrintListener)
    if (this.printMediaQueryList && this.printMediaListener) this.printMediaQueryList.removeListener(this.printMediaListener)
    this.afterPrintListener = null
    this.printMediaListener = null
    this.printMediaQueryList = null
  }

  render (h: CreateElement) {
    if (!this.workout) {
      return null
    }
    return <Card class={`workout-card workout-card-info ${SessionManager.Instance.isManager() && this.workout.hidden ? 'hidden-workout' : ''}`}
    click={this.selectWorkout} ref={`printWorkout${this.workout.id}`}>
    {this.renderRuns(h)}
    {this.renderHeader(h)}
    {this.renderPreview(h)}
    {this.renderTitle(h)}
    {this.renderSpecs(h)}
    {this.renderRight(h)}
  </Card>
  }

  renderRuns (h: CreateElement) {
    if (typeof this.runs !== 'number') return null
    return <WorkoutRuns slot='leading' runs={this.runs} />
  }

  renderHeader (h: CreateElement) {
    return <Overline slot='header' class='header'>
      <div class='tracked'>
        {this.workout.isCertified() ? <span><Icon name='workout/certified' />{this.$t('PAGE.CONTENT.WORKOUTS.TYPES.CERTIFIED_WORKOUT')} /&nbsp;</span> : ''}
        {!this.workout.isStationWorkout() ?
          <MobileTooltip content={this.$t('PAGE.CONTENT.WORKOUT.HOME_WORKOUT')}>
            <Icon class='home' name='workout/home' />
          </MobileTooltip> :
          <span>
            {this.workout.circleWorkout ?
              <MobileTooltip content={this.$t('PAGE.CONTENT.WORKOUT.CIRCULAR_WORKOUT')}>
                <Icon class='workout-type-icon' name='workout/circle-workout' />
              </MobileTooltip> :
              this.workout.continuousWorkout ?
                <MobileTooltip content={this.$t('PAGE.CONTENT.WORKOUT.CONTINUOUS_WORKOUT')}>
                  <Icon class='workout-type-icon' name='workout/continuous-workout' />
                </MobileTooltip> :
                <MobileTooltip content={this.$t('PAGE.CONTENT.WORKOUT.NON_CONTINUOUS_WORKOUT')}>
                  <Icon class='workout-type-icon' name='workout/non-continuous-workout' />
                </MobileTooltip>}
            {this.workout.stationLocked ?
              <MobileTooltip content={this.$t('PAGE.CONTENT.WORKOUT.STATION_DEPENDENT_WORKOUT')}>
                <Icon class='workout-type-icon' name='workout/station-dependent-workout' />
              </MobileTooltip> :
              <MobileTooltip content={this.$t('PAGE.CONTENT.WORKOUT.STATION_INDEPENDENT_WORKOUT')}>
                <Icon class='workout-type-icon' name='workout/station-independent-workout' />
              </MobileTooltip>}
          </span>}
        {SessionManager.Instance.isManager() && this.workout.hidden ?
          <MobileTooltip content={this.$t('PAGE.CONTENT.WORKOUT.HIDDEN_WORKOUT')}>
            <Icon class='hidden' name={'workout/hidden-workout'} />
          </MobileTooltip> : null}
      </div>
      <div class='content-to-print'>{this.workout.id}</div>
    </Overline>
  }

  renderPreview (h: CreateElement) {
    return <div slot='preview' class='workout-preview-big'>
      {this.previewImageProcessing ? <Loader /> : null}
      <img src={this.workout.displayImage} />
    </div>
  }

  renderTitle (h: CreateElement) {
    return <H6 class='title' slot='title'>
      {this.workout.name}
    </H6>
  }

  renderSpecs (h: CreateElement) {
    const firstItemsEquipment = this.workout.equipment.slice(0, 5).map((equipmentName) => {
      return <div class='equipment'>
         <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipmentName.toUpperCase())}>
           <Icon name={'equipment/' + equipmentName} />
         </Tooltip>
       </div>
    })

    const equipment = this.workout.equipment.map((equipmentName) => {
      return <div class='equipment'>
        <Icon name={'equipment/' + equipmentName} />
        <Body1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipmentName.toUpperCase())}</Body1>
      </div>
    })

    return <div slot='specs' class='specs'>
      <div class='workout-detail'>
        <Icon name='workout/exercises' />
        <Subtitle1 class='detail-text'>{this.workout.exerciseCount} {this.$t('EXERCISES')}</Subtitle1>
      </div>
      <div class='workout-detail'>
        <Icon name='workout/time' />
        <Subtitle1 class='detail-text'>{this.workout.estimatedDurationInMin} {this.$t('UNITS.MIN')}</Subtitle1>
      </div>
      <div class='workout-detail'>
        <Icon name='workout/score' />
        <Subtitle1 class='detail-text'>{this.workout.estimatedMetValue} {this.$t('UNITS.MET')}</Subtitle1>
      </div>

      <div class='workout-timeline'>{convertDateTo(this.workout.createdAt, 'MMM Do YYYY')}</div>
      <div class='workout-timeline'>{this.workout.platformUpdatedAt && this.workout.platformUpdatedAt !== this.workout.createdAt ?
        `${this.$t('UPDATED_AT')}: ${convertDateTo(this.workout.platformUpdatedAt, 'ddd, MMM Do, YYYY HH:mm A')}` : ''}
      </div>
      <div class='equipmentWrapper'>{firstItemsEquipment}{this.workout.equipment.length > 5 ?
        <Icon class='see-more' name='util/see-more' click={this.invertShowEquipment} /> : ''} </div>
      <Modal cssClass={'training-equipment-modal'} show={this.showEquipmentPopup} closeCallback={this.invertShowEquipment}
             title={this.$t('EQUIPMENT')}>
        {equipment}
      </Modal>
      <div class='content-to-print'>{this.renderQrCode(h)}</div>
    </div>
  }

  renderRight (h: CreateElement) {
    if (this.type === 'view') {
      return this.renderRightView(h)
    }
    if (this.type === 'active') {
      return this.renderRightActive(h)
    }
    if (this.type === 'select') {
      return this.renderRightSelect(h)
    }
    if (this.type === 'stats') {
      return this.renderRightStats(h)
    }
    return
  }

  renderRightStats (h: CreateElement) {
    return <div slot='right' class='more-options'>
      <MenuButton>
        <Popover>
          <OptionGroup>
            <div class='view-workout' onClick={this.goToViewWorkout}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.VIEW_WORKOUT')}</div>
          </OptionGroup>
        </Popover>
      </MenuButton>
      {this.renderUserInfo(h)}
    </div>
  }

  renderUserInfo (h: CreateElement) {
    if (!SessionManager.Instance.isManager()) return null
    return <div class='user-info-icon'>
      <Tooltip content={
        <div>
          <div>{`${this.$t('CREATED_AT')}: ${convertDateTo(this.workout.createdAt, 'MMM Do YYYY')}, ${this.$t('BY')} ${this.workout.createdBy}`}</div>
          {this.workout.platformUpdatedAt && this.workout.platformUpdatedBy && this.workout.platformUpdatedAt !== this.workout.createdAt &&
            <div>{`${this.$t('UPDATED_AT')}: ${convertDateTo(this.workout.platformUpdatedAt, 'ddd, MMM Do, YYYY HH:mm A')}, ${this.$t('BY')} ${this.workout.platformUpdatedBy}`}</div>
          }
        </div>
      }>
        <Icon name='util/user-info'/>
      </Tooltip>
    </div>
  }

  renderRightView (h: CreateElement) {
    const isCertified = this.workout.isCertified()
    return <div slot='right' class='more-options'>
      <MenuButton>
        <Popover>
          <OptionGroup>
            {<DropdownOption class='assign-to-member-header' label={this.$t('PAGE.CONTENT.TRAINING_PLAN.ASSIGN_TO_MEMBER')}>
              <AssignToMember hideDate={true} callback={this.assignToMember} />
            </DropdownOption>}
            {!isCertified && !SessionManager.Instance.isAssistant() ? <div class='edit-workout' onClick={this.edit}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.EDIT_WORKOUT')}</div> : null}
            <div class='view-workout' onClick={this.goToViewWorkout}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.VIEW_WORKOUT')}</div>
            {!isCertified && !SessionManager.Instance.isAssistant() ? <div class='remove-workout'
                                 onClick={() => this.showCloseConfirm(this.$t('PAGE.CONTENT.WORKOUTS.CONFIRM_REMOVE'))}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.REMOVE_WORKOUT')}</div> : null}
            {!SessionManager.Instance.isAssistant() ? <div class='clone-as-station' onClick={this.cloneAsStationWorkout}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.CLONE_TO_STATION')}</div> : null}
            {/*Hide home workouts*/}
            {/*<div class='clone-as-home' onClick={this.cloneAsHomeWorkout}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.CLONE_TO_HOME')}</div>*/}
            {!isCertified && !SessionManager.Instance.isAssistant() ? <div class='upload-preview-image' onClick={this.showPreviewImageUpload}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.CHANGE_PREVIEW_IMAGE')}</div> : null}
            {!isCertified ? <div class='add-categories' onClick={this.invertShowCategories}>{this.$t('PAGE.CONTENT.WORKOUTS.CATEGORIES.ADD_CATEGORIES')}</div> : null}
            <div class='print' onClick={() => this.print(this.workout.id)}>{this.$t('PAGE.CONTENT.WORKOUT.PRINT_WORKOUT')}</div>
            <div class='print-exercises' onClick={this.printExercises}>{this.$t('PAGE.CONTENT.WORKOUT.PRINT_EXERCISES')}</div>
            {this.type === 'view' && this.workout.hidden && !SessionManager.Instance.isAssistant() ?
              <div class={'show-workout'} onClick={this.show}>{this.$t('PAGE.CONTENT.WORKOUT.SHOW')}</div> : null}
            {this.type === 'view' && !this.workout.hidden && !SessionManager.Instance.isAssistant() ?
              <div class={'hide-workout'} onClick={this.hide}>{this.$t('PAGE.CONTENT.WORKOUT.HIDE')}</div> : null}
          </OptionGroup>
        </Popover>
      </MenuButton>
      { !SessionManager.Instance.isManager() ?
        null
        : <div class='user-info-icon'>
          <Tooltip content={
            <div>
              <div>{`${this.$t('CREATED_AT')}: ${convertDateTo(this.workout.createdAt, 'MMM Do YYYY')}, ${this.$t('BY')} ${this.workout.createdBy}`}</div>
              {this.workout.platformUpdatedAt && this.workout.platformUpdatedBy && this.workout.platformUpdatedAt && this.workout.platformUpdatedAt !== this.workout.createdAt &&
                <div>{`${this.$t('UPDATED_AT')}: ${convertDateTo(this.workout.platformUpdatedAt, 'ddd, MMM Do, YYYY HH:mm A')}, ${this.$t('BY')} ${this.workout.platformUpdatedBy}`}</div>
              }
            </div>
          }>
            <Icon name='util/user-info'/>
          </Tooltip>
        </div> }
      <ConfirmModal show={this.showConfirmPopup}
                    closeCallback={this.remove}
                    message={this.confirmMessage}
                    type={this.notificationType} />
      {this.showPreviewImage ? <PreviewImage item={this.workout} saveCallback={this.updatePreviewImage} /> : ''}
      {this.showPrintExercises ? <PrintExercises exercises={this.workoutExercises.map((woExercise) => woExercise.exercise)} workoutName={this.workout.name} /> : ''}
      {this.showLoading && <LoadingScreen /> }
      {this.showCategories ? <SelectCategories workout={this.workout} closeCallback={this.invertShowCategories}/> : ''}
      <div class='column'>
        {!isCertified && this.isShowFilter() ? (
          <PositionChanger
            total={this.workoutCount}
            position={this.workout.position}
            changePosition={this.handlePositionChange}
          />
        ) : null}
      </div>
    </div>
  }

  renderRightActive (h: CreateElement) {
    const editableWorkout = !this.readonly && !this.workout.isCertified()
    const permissionToEditWorkout = ((SessionManager.Instance.isManager() && !SessionManager.Instance.isAssistant()) || !SessionManager.Instance.isManager() && this.member.memberPermissions.createStationWorkout)
    return <div slot='right' class='more-options'>
      <MenuButton>
        <Popover>
          <OptionGroup>
            {editableWorkout &&
              // edit option should only be visible to trainers and members that have createStationWorkout permissions
                 permissionToEditWorkout ?
              <div class='edit-workout' onClick={this.edit}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.EDIT_WORKOUT')}</div> : null}
            {editableWorkout && SessionManager.Instance.isManager() && !SessionManager.Instance.isAssistant() ? <div class='clone-and-edit-workout' onClick={this.cloneAsMemberWorkout}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.CLONE_AND_EDIT_WORKOUT')}</div> : null}
            {editableWorkout && SessionManager.Instance.isManager() && !SessionManager.Instance.isAssistant() ? <div class='clone-as-station' onClick={this.cloneAsStationWorkout}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.CLONE_TO_STATION')}</div> : null}
            <div class='view-workout' onClick={this.goToViewWorkout}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.VIEW_WORKOUT')}</div>
            <div class='print-exercises' onClick={this.printExercises}>{this.$t('PAGE.CONTENT.WORKOUT.PRINT_EXERCISES')}</div>
            {!this.workout.default && !this.readonly && !SessionManager.Instance.isAssistant() ?
              <div class='remove-workout'
                   onClick={() => this.showCloseConfirm(this.$t('PAGE.CONTENT.WORKOUTS.CONFIRM_REMOVE'))}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.REMOVE_WORKOUT')}</div>
              : null}
          </OptionGroup>
        </Popover>
      </MenuButton>
      { !SessionManager.Instance.isManager() ?
        null
        : <div class='user-info-icon'>
          <Tooltip content={
            <div>
              <div>{`${this.$t('CREATED_AT')}: ${convertDateTo(this.workout.createdAt, 'MMM Do YYYY')}, ${this.$t('BY')} ${this.workout.createdBy}`}</div>
              {this.workout.platformUpdatedAt && this.workout.platformUpdatedBy && this.workout.platformUpdatedAt && this.workout.platformUpdatedAt !== this.workout.createdAt &&
                <div>{`${this.$t('UPDATED_AT')}: ${convertDateTo(this.workout.platformUpdatedAt, 'ddd, MMM Do, YYYY HH:mm A')}, ${this.$t('BY')} ${this.workout.platformUpdatedBy}`}</div>
              }
            </div>
          }>
            <Icon name='util/user-info'/>
          </Tooltip>
        </div> }
      <ConfirmModal show={this.showConfirmPopup}
                    closeCallback={this.remove}
                    message={this.confirmMessage}
                    type={this.notificationType} />
      {this.showPreviewImage ? <PreviewImage item={this.workout} saveCallback={this.updatePreviewImage} /> : ''}
      {this.showLoading && <LoadingScreen /> }
      {this.showPrintExercises ? <PrintExercises exercises={this.workoutExercises.map((woExercise) => woExercise.exercise)} workoutName={this.workout.name} /> : ''}
    </div>
  }

  clickOutside () {
    this.showWorkoutCardDropdownOptions = false
  }

  updateWorkoutCardDropdownOptionsVisibility () {
    this.showWorkoutCardDropdownOptions = !this.showWorkoutCardDropdownOptions
  }

  handlePositionChange (newPosition: number) {
    this.apiClient.put(`${API.TRAINERS_API.WORKOUTS}/${this.workout.id}/update_position`, snakecaseKeys({ position: newPosition, club_id: this.workout.holderId }))
      .then(() => {
        EventBus.trigger('reloadWorkouts')
      })
      .catch(error => console.error('Failed to update position or reload workouts', error))
  }

  isShowFilter () {
    if (!this.$store.state.workoutFilters) {
      return false
    }
    return this.$store.state.workoutFilters.type === 'show'
  }

  renderRightSelect (h: CreateElement) {
    let exercisesToRender = this.exercises.map((exercise, index) => {
      const showBreakpoint = this.workout.continuousWorkout && this.exBreakPoints[index] && index !== this.exercises.length - 1
      return <ExerciseCardInfo exercise={exercise} type='noActions' showBreakpoint={showBreakpoint}/>
    })

    let isTrainerAndWorkoutHasMemberHolderType = this.workout.isMemberTrainerWorkout() && SessionManager.Instance.isManager() && !SessionManager.Instance.isAssistant()

    return <div slot='right' class='more-options fit-content' v-click-outside={this.clickOutside} onClick={this.updateWorkoutCardDropdownOptionsVisibility}>
      <MenuButton>
        <Popover>
          <OptionGroup>
            <div class='view-exercises-btn'
                 onClick={this.showExercises}>{this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.VIEW_EXERCISES')}</div>
            {SessionManager.Instance.isManager() && !SessionManager.Instance.isAssistant() && <div class='view-exercises-btn'
                 onClick={this.cloneAsStationWorkout}>{this.$t('PAGE.CONTENT.WORKOUTS.ACTIONS.CLONE_TO_STATION')}</div>}
            {isTrainerAndWorkoutHasMemberHolderType && <div class='view-exercises-btn'
                                                            onClick={this.workout.hidden ? this.show : this.hide}>{this.workout.hidden ? this.$t('PAGE.CONTENT.WORKOUT.SHOW') : this.$t('PAGE.CONTENT.WORKOUT.HIDE')}</div>}
            {isTrainerAndWorkoutHasMemberHolderType && <div class='view-exercises-btn'
                                                            onClick={() => this.showCloseConfirm(this.$t('PAGE.CONTENT.WORKOUTS.CONFIRM_REMOVE'))}>{this.$t('DELETE')}</div>}
          </OptionGroup>
        </Popover>
      </MenuButton>
      { !SessionManager.Instance.isManager() ?
        null
        : <div class='user-info-icon'>
          <Tooltip content={
            <div>
              <div>{`${this.$t('CREATED_AT')}: ${convertDateTo(this.workout.createdAt, 'MMM Do YYYY')}, ${this.$t('BY')} ${this.workout.createdBy}`}</div>
              {this.workout.platformUpdatedAt && this.workout.platformUpdatedBy && this.workout.platformUpdatedAt && this.workout.platformUpdatedAt !== this.workout.createdAt &&
                <div>{`${this.$t('UPDATED_AT')}: ${convertDateTo(this.workout.platformUpdatedAt, 'ddd, MMM Do, YYYY HH:mm A')}, ${this.$t('BY')} ${this.workout.platformUpdatedBy}`}</div>
              }
            </div>
          }>
            <Icon name='util/user-info'/>
          </Tooltip>
        </div> }
      {this.isSelected ? (
        <div style={{ marginTop: '10px' }}>
          <Icon class='selected-btn' name='/util/selected'click={this.selectWorkout} />
        </div>) : ''}

      <ConfirmModal show={this.showConfirmPopup}
                    closeCallback={this.remove}
                    message={this.confirmMessage}
                    type={this.notificationType} />
      <Modal class='exercises-modal' show={this.showExercisesPopup} closeCallback={this.hideExercises}
             title={this.$t('EXERCISES')}>
        <div class={'exercises'}>
          {exercisesToRender}
        </div>
      </Modal>
    </div>
  }

  renderQrCode (h: CreateElement) {
    return <QrcodeVue value={`pixWorkout_${this.workout.id}`} size={120} class='generated-qr-code' />
  }

  printExercises () {
    if (this.workout.id) {
      this.showLoading = true
      return this.service.getWorkout(this.workout.id)
        .then((response: any) => {
          this.workoutExercises = response.workout.workout_exercises
          this.$el.classList.add('print-custom')
          this.showPrintExercises = true
          this.showLoading = false
        })
    }
  }

  async remove (confirm) {
    if (confirm === true && this.workout.id) {
      if (this.workout.holderType === HolderType.CLUB && this.type === 'active') {
        this.onRemoveSuccessful()
      } else {
        const memberId = this.workout.isMemberTrainerWorkout() ? this.workout.holderId : 0
        this.service.delete(this.workout.id, memberId)
          .then(() => this.onRemoveSuccessful())
          .catch(error => console.error('failed to remove workout', error))
      }
    }
    this.showConfirmPopup = false
  }

  onRemoveSuccessful () {
    EventBus.trigger('reloadWorkouts')
    if (this.type === 'view' || this.type === 'select') {
      if (this.updateCallback && this.workout.id) {
        const memberId = this.workout.isMemberTrainerWorkout() && SessionManager.Instance.isManager() ? this.workout.holderId : null
        this.updateCallback(this.workout.id, memberId)
      }
      this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.WORKOUT.DELETE_SUCCESS'), fadeOut: true })
    } else if (this.type === 'active') {
      // reload to show the default activated workout (default workout is assigned after delete)
      EventBus.trigger('reloadSelectedMember')
      this.$store.dispatch('showToast', { message: this.$t('ACKNOWLEDGEMENT.SUCCESSFUL_CHANGES'), fadeOut: true })
    }
  }

  invertShowEquipment () {
    this.showEquipmentPopup = !this.showEquipmentPopup
  }

  edit () {
    if (this.type === 'active' && this.member) this.$store.dispatch('setMemberForActiveWorkout', this.member)
    this.$router.push(`${SessionManager.Instance.getBaseRoute()}/workout/edit/${this.workout.id}`)
  }

  goToViewWorkout () {
    this.$router.push(`${SessionManager.Instance.getBaseRoute()}/workout/view/${this.workout.id}`)
  }

  cloneAsMemberWorkout () {
    if (this.type === 'active' && this.member) this.$store.dispatch('setMemberForActiveWorkout', this.member)
    this.openWorkout(WorkoutType.CUSTOM)
  }

  cloneAsStationWorkout () {
    this.openWorkout(WorkoutType.CUSTOM)
  }

  cloneAsHomeWorkout () {
    this.openWorkout(WorkoutType.HOME)
  }

  /**
   * Retrieve the workout details and open the workout builder.
   */
  openWorkout (workoutType: WorkoutType) {
    if (this.workout.id) {
      this.service.getWorkout(this.workout.id)
        .then(response => this.onRetrieveWorkoutSuccessful(response, workoutType))
        .catch(error => console.error('failed to retrieve workout', error))
    }
  }

  onRetrieveWorkoutSuccessful (response, workoutType: WorkoutType) {
    let exercises: any[] = []
    if (response.workout && response.workout.workout_exercises) {
      exercises = response.workout.workout_exercises.map((workoutExercise) => {
        const exercise = new ExerciseResponse(camelcaseKeys(workoutExercise.exercise, { deep: true }))
        let formattedWorkoutExercise: WorkoutExercise = new WorkoutExercise(camelcaseKeys(workoutExercise, { deep: true }))
        formattedWorkoutExercise.setExercise(exercise)
        return formattedWorkoutExercise
      })
    }

    let newWorkout = this.createNewWorkoutData(workoutType, exercises)
    this.$store.dispatch('setSelectedWorkout', { workout: newWorkout, exercises: exercises })

    this.$router.push('/trainers/workout')
  }

  createNewWorkoutData (workoutType: WorkoutType, exercises) {
    let workoutToEdit = new WorkoutData(camelcaseKeys(this.workout, { deep: true }))
    workoutToEdit.id = null
    workoutToEdit.type = workoutType
    if (workoutType === WorkoutType.HOME) {
      // remove station workout properties
      delete (workoutToEdit as any).continuousWorkout
      delete (workoutToEdit as any).circleWorkout
      delete (workoutToEdit as any).stationLocked
      exercises.forEach(exercise => {
        delete exercise.stationId
      })
    }
    return workoutToEdit
  }

  /**
   * Create member owned workout copy; Activate the new workout to the member
   *
   * Steps:
   * - retrieve the workout details
   * - build a copy as  member owned workout
   * - submit new workout
   * - activate workout for member
   *
   * @param member
   */
  assignToMember (member: Member) {
    if (!member) {
      return
    }
    if (member.id && this.workout.id) {

      // get the workout details
      this.service.getWorkout(this.workout.id)
        .then((response: any) => {
          let exercises: any[] = []
          if (response.workout && response.workout.workout_exercises) {
            exercises = response.workout.workout_exercises.map((workoutExercise) => {
              const exercise = new ExerciseResponse(camelcaseKeys(workoutExercise.exercise, { deep: true }))
              let formattedWorkoutExercise: WorkoutExercise = new WorkoutExercise(camelcaseKeys(workoutExercise, { deep: true }))
              formattedWorkoutExercise.setExercise(exercise)
              return formattedWorkoutExercise
            })
          }

          // build the workout copy (the member was checked above)
          const memberWorkout = this.service.buildWorkout(this.workout, exercises, this.$store.state.club.screenExerciseLen, this.$store.state.stations,
            HolderType.MEMBER, this.$store.state.club.id, member ? member.id : /* istanbul ignore next */ null)

          // create the member owned workout
          this.apiClient.post(API.TRAINERS_API.WORKOUTS, { workout: memberWorkout })
            .then((response: any) => {

              // activate workout
              this.apiClient
                .post(API.TRAINERS_API.WORKOUT_ACTIVATE.replace('{workoutId}', `${response.workout.id}`).replace('{memberId}', `${member.id}`), {})
                .then(() => {
                  this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.WORKOUT.ASSIGN_NOTIFICATION', { name: member.fullName }), fadeOut: true })
                })
                .catch((error) => {
                  this.setErrors(error)
                })
            })
            .catch((error) => console.log('cannot add workout', error))
        })
        .catch((error) => console.error('failed to retrieve workout', error))
    } else {
      this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.ASSIGNATION_FAILED'), fadeOut: false })
    }
  }

  showPreviewImageUpload () {
    this.showPreviewImage = !this.showPreviewImage
  }

  updatePreviewImage (image?: PreviewImageDTO | DefaultPreviewImageDTO | null): any {
    this.showPreviewImageUpload()

    if (typeof image === 'undefined' || !this.workout.id) {
      return
    }

    this.previewImageProcessing = true

    // get the workout details
    return this.service.getWorkout(this.workout.id)
      .then((response: any) => {
        let exercises: any[] = []
        if (response.workout && response.workout.workout_exercises) {
          exercises = response.workout.workout_exercises.map((workoutExercise) => {
            const exercise = new ExerciseResponse(camelcaseKeys(workoutExercise.exercise, { deep: true }))
            let formattedWorkoutExercise: WorkoutExercise = new WorkoutExercise(camelcaseKeys(workoutExercise, { deep: true }))
            formattedWorkoutExercise.setExercise(exercise)
            return formattedWorkoutExercise
          })
        }

        // build the edited workout
        let workout = this.service.buildWorkout(this.workout, exercises, this.$store.state.club.screenExerciseLen, this.$store.state.stations,
          this.workout.holderType, this.$store.state.club.id, null)
        if (image !== null && typeof (image as Partial<DefaultPreviewImageDTO>).defaultPreviewImage === 'number') {
          workout.defaultPreviewImage = (image as DefaultPreviewImageDTO).defaultPreviewImage
        } else {
          workout.previewImage = image
        }

        return this.apiClient.put(`${API.TRAINERS_API.WORKOUTS}/${this.workout.id}`, { workout: snakecaseKeys(workout) })
          .then((response: any) => {
            // display the updated image
            this.workout.previewImage = response.workout.preview_image
            this.workout.defaultPreviewImage = response.workout.default_preview_image
            this.workout.setDefaultDisplayImage()
          })
          .finally(() => this.previewImageProcessing = false)
      })
  }

  print (workoutId) {
    this.$el.classList.remove('print-custom')
    let card = this.$refs[`printWorkout${workoutId}`] as Vue
    if (card) {
      // add a class to the selected workout card, to be able to style and show the content in the print view
      card.$el.classList.add('workout-to-print')
      window.print()
    }
  }

  resetPrint () {
    this.showPrintExercises = false
    this.$el.classList.remove('print-custom')
    let card = this.$refs[`printWorkout${this.workout.id}`] as Vue
    if (card) {
      // remove the class of the selected workout card to be printed
      card.$el.classList.remove('workout-to-print')
    }
  }

  selectWorkout () {
    if (this.type === 'select') {
      const id = this.isSelected ? null : this.workout.id
      this.updateCallback(id, null)
    }
  }

  protected showCloseConfirm (message: any) {
    this.confirmMessage = message
    this.notificationType = NotificationTypes.CONFIRM
    this.showConfirmPopup = true
  }

  protected setErrors (error) {
    switch (error.status) {
      // TO DO integration with BE
    }
  }

  protected show () {
    if (this.workout.id) {
      const memberId = this.workout.isMemberTrainerWorkout() ? this.workout.holderId : 0
      return this.service.toggleVisibility(this.workout.id, memberId)
        .then(() => {
          this.workout.hidden = false
          if (memberId) {
            const key = 'PAGE.CONTENT.WORKOUT.SHOW_SUCCESS_MEMBER'
            // vue-i18n always provides $te
            const hasKey = (this as any).$te ? (this as any).$te(key) : /* istanbul ignore next */ false
            const message = hasKey ? this.$t(key) : 'Workout will be shown for the user.'
            this.$store.dispatch('showToast', { message, fadeOut: true })
          } else {
            this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.WORKOUT.SHOW_SUCCESS'), fadeOut: true })
          }
          EventBus.trigger('reloadWorkouts')
        })
    }
  }

  protected hide () {
    if (this.workout.id) {
      const memberId = this.workout.isMemberTrainerWorkout() ? this.workout.holderId : 0
      return this.service.toggleVisibility(this.workout.id, memberId)
        .then(() => {
          this.workout.hidden = true
          if (memberId) {
            const key = 'PAGE.CONTENT.WORKOUT.HIDE_SUCCESS_MEMBER'
            // vue-i18n always provides $te
            const hasKey = (this as any).$te ? (this as any).$te(key) : /* istanbul ignore next */ false
            const message = hasKey ? this.$t(key) : "Workout won't be shown for the user."
            this.$store.dispatch('showToast', { message, fadeOut: true })
          } else {
            this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.WORKOUT.HIDE_SUCCESS'), fadeOut: true })
          }
          EventBus.trigger('reloadWorkouts')
        })
    }
  }

  protected showExercises () {
    if (this.workout.id) {
      this.showLoading = true
      return this.service.getWorkout(this.workout.id)
        .then((response: any) => {
          this.exBreakPoints = response.workout.workout_exercises.map((woEx) => woEx.breakpoint_after_exercise)
          this.exercises = response.workout.workout_exercises.map((workoutExercise) => new ExerciseResponse(camelcaseKeys(workoutExercise.exercise, { deep: true })))
          this.showLoading = false
          this.showExercisesPopup = true
        })
    }
    return undefined
  }

  protected hideExercises () {
    this.showExercisesPopup = false
  }

  private invertShowCategories () {
    this.showCategories = !this.showCategories
  }
}
